'use client'

import { useState, useEffect, Suspense } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { ShopLayout } from '@/components/shop/ShopLayout'
import { ProductGrid } from '@/components/shop/ProductGrid'
import { getProducts, ProductListItem } from '@/lib/api'

function SearchContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const initialQuery = searchParams.get('q') ?? ''

  const [query, setQuery] = useState(initialQuery)
  const [products, setProducts] = useState<ProductListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    const q = searchParams.get('q') ?? ''
    setQuery(q)
    if (!q.trim()) {
      setProducts([])
      setSearched(false)
      return
    }
    setLoading(true)
    setSearched(true)
    getProducts({ search: q })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false))
  }, [searchParams])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = query.trim()
    router.push(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search')
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">Sök produkter</h1>
      <form onSubmit={submit} className="relative max-w-xl mb-8">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Sök efter produkter, kategorier…"
          autoFocus
          className="w-full h-12 pl-12 pr-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 dark:focus:border-white"
        />
        <svg className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      </form>

      {loading ? (
        <div className="py-16 text-center text-neutral-500">Söker…</div>
      ) : searched ? (
        <>
          <p className="text-sm text-neutral-500 mb-6">
            {products.length} resultat för &ldquo;{searchParams.get('q')}&rdquo;
          </p>
          <ProductGrid products={products} emptyMessage="Inga produkter matchade din sökning." />
        </>
      ) : (
        <p className="text-neutral-500">Skriv något för att söka i sortimentet.</p>
      )}
    </div>
  )
}

export default function SearchPage() {
  return (
    <ShopLayout>
      <Suspense fallback={<div className="max-w-7xl mx-auto px-4 py-10 text-neutral-500">Laddar…</div>}>
        <SearchContent />
      </Suspense>
    </ShopLayout>
  )
}
